"use client"
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useDisclosure,
  useToast,
  Box,
  Skeleton,
} from "@chakra-ui/react"
import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  addITskillAsync,
  deleteITskillAsync,
  fetchITskillsAsync,
  selectITskills,
  updateITskillAsync,
} from "./ITskillSlice"
import { useForm } from "react-hook-form"
import { fetchJobSkillsAsync, selectJobSkills } from "../../../AdminPanel/JobPostingSlice/JobSkillsList/JobSkillsSlice"
import SpinnerLoad from "../../../common/Spinner"
import { ExclamationCircleIcon } from "@heroicons/react/24/solid"
import EmptyState from "../../components/Tester"
import { FaLaptopCode } from "react-icons/fa"

function ITskills() {
  const dispatch = useDispatch()
  const ITskills = useSelector(selectITskills)
  const skills = useSelector(selectJobSkills)

  const [selectedSkill, setSelectedSkill] = useState(null)
  const [duplicateError, setDuplicateError] = useState(false)
  const [loading, setLoading] = useState(false) // submit/delete state
  const [isInitialLoading, setIsInitialLoading] = useState(true) // skeleton control
  const [deletingITskill, setDeletingITskill] = useState(null)

  const toast = useToast()
  const hasInitiatedFetch = useRef(false)

  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  // Form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      skill_uuid: "",
      versions: "",
      experience_year: "",
      experience_month: "",
    },
  })

  // Helper to fetch both skills and IT skills with skeleton
  const refreshData = async () => {
    setIsInitialLoading(true)
    try {
      await dispatch(fetchITskillsAsync())
      await dispatch(fetchJobSkillsAsync())
    } finally {
      setIsInitialLoading(false)
    }
  }

  useEffect(() => {
    if (!hasInitiatedFetch.current && (!ITskills || ITskills.length === 0 || !skills || skills.length === 0)) {
      hasInitiatedFetch.current = true
      refreshData()
    } else {
      setIsInitialLoading(false)
    }
  }, [dispatch, ITskills, skills])

  // Duplicates handling
  const watchedSkillUuid = watch("skill_uuid")
  useEffect(() => {
    if (!watchedSkillUuid || !Array.isArray(ITskills)) {
      setDuplicateError(false)
      return
    }
    // Ignore current selection during edit
    const dup = ITskills.some(
      (s) => s?.skill?.uuid === watchedSkillUuid && (!selectedSkill || s?.skill?.uuid !== selectedSkill?.skill?.uuid),
    )
    setDuplicateError(dup)
  }, [watchedSkillUuid, ITskills, selectedSkill])

  const months = Array.from({ length: 13 }, (_, i) => ({ label: String(i), value: i }))

  // Open Add modal (clean form)
  const openAddModal = () => {
    reset({
      skill_uuid: "",
      versions: "",
      experience_year: "",
      experience_month: "",
    })
    setSelectedSkill(null)
    setDuplicateError(false)
    onAddOpen()
  }

  // Open Edit modal (prefill with selected ITskill)
  const openUpdateModal = (ITskill) => {
    if (!ITskill) return
    setSelectedSkill(ITskill)
    // Prefill fields
    reset({
      skill_uuid: ITskill?.skill?.uuid || "",
      versions: ITskill?.versions || "",
      experience_year: ITskill?.experience_year ?? "",
      experience_month: ITskill?.experience_month ?? "",
    })
    onEditOpen()
  }

  // Delete modal open
  const openDeleteModal = (ITskill) => {
    setDeletingITskill(ITskill)
    onDeleteOpen()
  }

  // Save (Add)
  const handleSave = async (newITskill) => {
    if (duplicateError) {
      toast({
        title: "Duplicate Skill",
        description: "You have already selected this skill",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setLoading(true)
    try {
      await dispatch(addITskillAsync({ newITskill }))
      await refreshData()
      onAddClose()
      reset()
      toast({
        title: "Skill Added",
        description: "Your IT skill has been added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to add IT skills", error)
      toast({
        title: "Error",
        description: "Failed to add IT skill",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  // Update
  const handleUpdate = async (ITskillData) => {
    if (duplicateError) {
      toast({
        title: "Duplicate Skill",
        description: "You have already selected this skill",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setLoading(true)
    if (selectedSkill) {
      try {
        await dispatch(
          updateITskillAsync({
            uuid: selectedSkill.uuid,
            ITskillData,
          }),
        )
        await refreshData()
        setSelectedSkill(null)
        reset()
        onEditClose()
        toast({
          title: "Skill Updated",
          description: "Your IT skill has been updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } catch (error) {
        console.error("Failed to update IT skill", error)
        toast({
          title: "Error",
          description: "Failed to update IT skill",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } finally {
        setLoading(false)
      }
    }
  }

  // Delete
  const handleDelete = async (uuid) => {
    setLoading(true)
    try {
      await dispatch(deleteITskillAsync({ uuid }))
      await refreshData()
      onDeleteClose()
      toast({
        title: "Skill Deleted",
        description: "Your IT skill has been deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to delete IT skill", error)
      toast({
        title: "Error",
        description: "Failed to delete IT skill",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setLoading(false)
    }
  }

  // Validation helpers
  const validateYears = (value) => {
    if (value === "" || value === undefined || value === null) return "Experience in years is required"
    const n = Number(value)
    if (!Number.isFinite(n) || n < 0 || n > 30) return "Years must be between 0 and 30"
    return true
  }
  const validateMonths = (value) => {
    if (value === "" || value === undefined || value === null) return "Experience in months is required"
    const n = Number(value)
    if (!Number.isFinite(n) || n < 0 || n > 12) return "Months must be between 0 and 12"
    return true
  }
  const validateVersion = (value) => {
    if (!value) return true
    const v = String(value).trim()
    if (v.length > 50) return "Version must be 50 characters or less"
    if (!/^[A-Za-z0-9.\-+ ]+$/.test(v)) return "Version can include letters, numbers, dot, hyphen, plus"
    return true
  }

  // One flag for both modals to disable Save on duplicates
  const isDuplicateActive = !!duplicateError

  return (
    <div className="bg-mainBg" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
        {/* Header */}
        <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div className="flex items-start gap-2">
            <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FaLaptopCode />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Skills</h1>
              <p className="text-xs text-gray-500">Add and manage your IT skills</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button colorScheme="blue" onClick={openAddModal} className="whitespace-nowrap shadow">
              {Array.isArray(ITskills) && ITskills.length === 0 ? "Add Your Skills" : "Add"}{" "}
              <i className="fa-solid fa-plus pl-2"></i>
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full rounded-lg">
            <tbody>
              {isInitialLoading ? (
                // Skeleton table rows
                <>
                  {[...Array(3)].map((_, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="px-6 py-3 w-1/3">
                        <Box>
                          <Skeleton height="16px" mb="2" />
                          <Skeleton height="12px" width="60%" />
                        </Box>
                      </td>
                      <td className="px-6 py-3 w-1/3">
                        <Box>
                          <Skeleton height="16px" mb="2" />
                          <Skeleton height="12px" width="40%" />
                        </Box>
                      </td>
                      <td className="px-6 py-3 w-1/3">
                        <Box className="flex gap-2">
                          <Skeleton height="32px" width="72px" />
                          <Skeleton height="32px" width="72px" />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </>
              ) : Array.isArray(ITskills) && ITskills.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-12">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyState
                        title="Skills"
                        icon={FaLaptopCode}
                        emptyMessage="Start by adding your first skill"
                        addButtonText="Add Your First Skill"
                        onAdd={openAddModal}
                      />
                    </div>
                  </td>
                </tr>
              ) : ITskills && ITskills.length > 0 ? (
                <>
                  <tr className="bg-gray-100">
                    <th className="px-6 py-3 text-left text-gray-700 font-semibold">Skill</th>
                    <th className="px-6 py-3 text-left text-gray-700 font-semibold">Experience</th>
                    <th className="px-6 py-3 text-left text-gray-700 font-semibold">Actions</th>
                  </tr>
                  {ITskills.map((ITskill, index) => (
                    <tr key={index} className="hover:bg-mainBg transition duration-200 border-b">
                      <td className="px-6 py-3 font-medium text-black text-sm">
                        {ITskill?.skill?.title}
                        {ITskill?.versions ? (
                          <span className="text-gray-500 font-normal ml-2">• v{ITskill.versions}</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3 text-gray-600 font-medium text-sm">
                        {ITskill.experience_year} Years {ITskill.experience_month} months
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2 items-center">
                          <Button onClick={() => openUpdateModal(ITskill)} colorScheme="blue" className="shadow-sm">
                            <i className="fa-sharp fa-solid fa-pen"></i>
                          </Button>
                          <Button colorScheme="red" variant="outline" onClick={() => openDeleteModal(ITskill)}>
                            <i className="cursor-pointer fa-solid fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-3 text-center text-gray-500 font-medium">
                    No Skills Added Yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent padding={3}>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton color="gray.500" />
          <ModalBody>
            <h4 className="text-sm sm:text-base text-gray-800">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{deletingITskill?.skill?.title}</span> skill?
            </h4>
            {loading && <SpinnerLoad />}
          </ModalBody>
          <ModalFooter gap={4}>
            <Button
              colorScheme="red"
              onClick={() => deletingITskill && handleDelete(deletingITskill.uuid)}
              isDisabled={loading || !deletingITskill}
            >
              Delete
            </Button>
            <Button variant="outline" onClick={onDeleteClose} isDisabled={loading}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Shared Modal UI for Add and Edit */}
      <Modal isOpen={isAddOpen} onClose={() => { reset(); onAddClose(); }} isCentered closeOnOverlayClick={!loading}>
        <ModalOverlay />
        <ModalContent maxW="5xl">
          <form onSubmit={handleSubmit(handleSave)} noValidate>
            <ModalHeader>IT Skill</ModalHeader>
            <ModalCloseButton isDisabled={loading} />
            <ModalBody pb={6}>
              <FormControl isInvalid={isDuplicateActive || !!errors.skill_uuid} isRequired>
                <FormLabel>Skill Title</FormLabel>
                <Select
                  placeholder="Select Skill Title"
                  aria-invalid={!!errors.skill_uuid || isDuplicateActive}
                  {...register("skill_uuid", { required: "Skill title is required" })}
                >
                  {skills &&
                    skills.map((skill) => (
                      <option key={skill.uuid} value={skill.uuid}>
                        {skill.title}
                      </option>
                    ))}
                </Select>
                {isDuplicateActive && (
                  <div className="flex items-center mt-2 text-red-500 text-sm">
                    <ExclamationCircleIcon className="h-5 w-5 mr-1" />
                    <span>You have already selected this skill</span>
                  </div>
                )}
                {errors.skill_uuid && <p className="text-red-500 text-sm mt-1">{errors.skill_uuid.message}</p>}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.versions}>
                <FormLabel>Skill Version (Optional)</FormLabel>
                <Input
                  placeholder="e.g. 16.13.1"
                  aria-invalid={!!errors.versions}
                  {...register("versions", {
                    validate: validateVersion,
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                {errors.versions && <p className="text-red-500 text-sm mt-1">{errors.versions.message}</p>}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.experience_year} isRequired>
                <FormLabel>Experience in Years</FormLabel>
                <Select
                  placeholder="Select Experience in Years"
                  aria-invalid={!!errors.experience_year}
                  {...register("experience_year", {
                    required: "Experience in years is required",
                    validate: validateYears,
                  })}
                >
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </Select>
                {errors.experience_year && (
                  <p className="text-red-500 text-sm mt-1">{errors.experience_year.message}</p>
                )}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.experience_month} isRequired>
                <FormLabel>Experience in Months</FormLabel>
                <Select
                  placeholder="Select Experience in Months"
                  aria-invalid={!!errors.experience_month}
                  {...register("experience_month", {
                    required: "Experience in months is required",
                    validate: validateMonths,
                  })}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                {errors.experience_month && (
                  <p className="text-red-500 text-sm mt-1">{errors.experience_month.message}</p>
                )}
              </FormControl>
            </ModalBody>
            {loading && <SpinnerLoad />}
            <ModalFooter>
              <Button mr={3} type="submit" isDisabled={isDuplicateActive || loading}>
                Save
              </Button>
              <Button
                onClick={() => {
                  reset()
                  onAddClose()
                }}
                isDisabled={loading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditOpen} onClose={() => { reset(); onEditClose(); }} isCentered closeOnOverlayClick={!loading}>
        <ModalOverlay />
        <ModalContent maxW="5xl">
          <form onSubmit={handleSubmit(handleUpdate)} noValidate>
            <ModalHeader>IT Skill</ModalHeader>
            <ModalCloseButton isDisabled={loading} />
            <ModalBody pb={6}>
              <FormControl isInvalid={isDuplicateActive || !!errors.skill_uuid} isRequired>
                <FormLabel>Skill Title</FormLabel>
                <Select
                  placeholder="Select Skill Title"
                  aria-invalid={!!errors.skill_uuid || isDuplicateActive}
                  {...register("skill_uuid", { required: "Skill title is required" })}
                >
                  {skills &&
                    skills.map((skill) => (
                      <option key={skill.uuid} value={skill.uuid}>
                        {skill.title}
                      </option>
                    ))}
                </Select>
                {isDuplicateActive && (
                  <div className="flex items-center mt-2 text-red-500 text-sm">
                    <ExclamationCircleIcon className="h-5 w-5 mr-1" />
                    <span>You have already selected this skill</span>
                  </div>
                )}
                {errors.skill_uuid && <p className="text-red-500 text-sm mt-1">{errors.skill_uuid.message}</p>}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.versions}>
                <FormLabel>Skill Version (Optional)</FormLabel>
                <Input
                  placeholder="e.g. 16.13.1"
                  aria-invalid={!!errors.versions}
                  {...register("versions", {
                    validate: validateVersion,
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                {errors.versions && <p className="text-red-500 text-sm mt-1">{errors.versions.message}</p>}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.experience_year} isRequired>
                <FormLabel>Experience in Years</FormLabel>
                <Select
                  placeholder="Select Experience in Years"
                  aria-invalid={!!errors.experience_year}
                  {...register("experience_year", {
                    required: "Experience in years is required",
                    validate: validateYears,
                  })}
                >
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </Select>
                {errors.experience_year && (
                  <p className="text-red-500 text-sm mt-1">{errors.experience_year.message}</p>
                )}
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.experience_month} isRequired>
                <FormLabel>Experience in Months</FormLabel>
                <Select
                  placeholder="Select Experience in Months"
                  aria-invalid={!!errors.experience_month}
                  {...register("experience_month", {
                    required: "Experience in months is required",
                    validate: validateMonths,
                  })}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                {errors.experience_month && (
                  <p className="text-red-500 text-sm mt-1">{errors.experience_month.message}</p>
                )}
              </FormControl>
            </ModalBody>

            {loading && <SpinnerLoad />}

            <ModalFooter>
              <Button mr={3} type="submit" isDisabled={isDuplicateActive || loading}>
                Update
              </Button>
              <Button
                onClick={() => {
                  reset()
                  onEditClose()
                }}
                isDisabled={loading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </div>
  )
}
export default ITskills